const ensureString = (value) => {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  try {
    return String(value);
  } catch {
    return null;
  }
};

const ensureTrimmedString = (value) => {
  const str = ensureString(value);
  if (str == null) return null;
  const trimmed = str.trim();
  return trimmed ? trimmed : null;
};

const ensureNumber = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const pickFirstValue = (input, keys) => {
  if (!input || typeof input !== 'object') return null;
  for (const key of keys) {
    if (key in input && input[key] != null) {
      return input[key];
    }
  }
  return null;
};

const normalizeMsgType = (value) => {
  const msgType = ensureTrimmedString(value);
  if (!msgType) return null;

  const canonical = msgType.toLowerCase();
  if (canonical === 'application' || canonical === 'public_message') {
    return canonical;
  }

  return msgType;
};

const normalizeWelcomes = (input) => {
  if (!input || typeof input !== 'object') return null;
  const sources = [];
  if (Array.isArray(input.welcomes)) sources.push(input.welcomes);
  if (Array.isArray(input.pendingWelcomes)) sources.push(input.pendingWelcomes);
  if (Array.isArray(input.pending_welcomes)) sources.push(input.pending_welcomes);

  if (!sources.length) {
    return null;
  }

  const normalized = [];
  for (const entries of sources) {
    for (const entry of entries) {
      if (!entry || typeof entry !== 'object') continue;
      const welcomeB64 = ensureTrimmedString(entry.welcomeB64 ?? entry.welcome_b64);
      if (!welcomeB64) continue;

      const normalizedEntry = { welcome_b64: welcomeB64 };

      const ratchetTree = ensureTrimmedString(entry.ratchetTreeB64 ?? entry.ratchet_tree_b64);
      if (ratchetTree) {
        normalizedEntry.ratchet_tree_b64 = ratchetTree;
      }

      const toUserId = ensureTrimmedString(entry.toUserId ?? entry.to_user_id);
      if (toUserId) {
        normalizedEntry.to_user_id = toUserId;
      }

      const toClientId = ensureTrimmedString(entry.toClientId ?? entry.to_client_id);
      if (toClientId) {
        normalizedEntry.to_client_id = toClientId;
      }

      const entryEpoch = ensureNumber(entry.epoch);
      if (entryEpoch != null) {
        normalizedEntry.epoch = entryEpoch;
      } else {
        const epochString = ensureTrimmedString(entry.epoch);
        if (epochString) {
          normalizedEntry.epoch = epochString;
        }
      }

      normalized.push(normalizedEntry);
    }
  }

  return normalized.length ? normalized : null;
};

export const serializeMlsMessagePayload = (input = {}) => {
  const payload = {};

  const assign = (keys, targetKey, normalizer = ensureTrimmedString) => {
    const value = pickFirstValue(input, keys);
    if (value == null) return;
    const normalizedValue = normalizer(value);
    if (normalizedValue != null) {
      payload[targetKey] = normalizedValue;
    }
  };

  assign(['senderClientId', 'sender_client_id'], 'sender_client_id');
  assign(['envelopeB64', 'envelope_b64'], 'envelope_b64');
  assign(['ciphertextB64', 'ciphertext_b64', 'ciphertext'], 'ciphertext_b64');
  assign(['msgType', 'msg_type'], 'msg_type', normalizeMsgType);
  assign(['contentType', 'content_type'], 'content_type');
  assign(['clientMsgId', 'client_msg_id'], 'client_msg_id');
  assign(['historyCiphertextB64', 'history_ciphertext_b64'], 'history_ciphertext_b64');
  assign(['historyIvB64', 'history_iv_b64'], 'history_iv_b64');
  assign(['historyAlg', 'history_alg'], 'history_alg');
  if (
    payload.ciphertext_b64 == null
    && payload.envelope_b64 != null
    && (payload.msg_type == null || payload.msg_type === 'application')
  ) {
    payload.ciphertext_b64 = payload.envelope_b64;
  }

  const welcomes = normalizeWelcomes(input);
  if (welcomes) {
    payload.welcomes = welcomes;
  }

  if (payload.msg_type === 'public_message') {
    const sanitizedProbePayload = {};
    const probeEpoch = ensureNumber(pickFirstValue(input, ['epoch']));
    if (payload.sender_client_id != null) {
      sanitizedProbePayload.sender_client_id = payload.sender_client_id;
    }
    if (payload.client_msg_id != null) {
      sanitizedProbePayload.client_msg_id = payload.client_msg_id;
    }
    if (payload.envelope_b64 != null) {
      sanitizedProbePayload.envelope_b64 = payload.envelope_b64;
    }
    if (probeEpoch != null) {
      sanitizedProbePayload.epoch = probeEpoch;
    }
    sanitizedProbePayload.msg_type = 'public_message';
    return sanitizedProbePayload;
  }

  return payload;
};

export const prepareMlsMessagePayload = (input = {}, { context = null } = {}) => {
  const payload = serializeMlsMessagePayload(input);
  const hasSender = typeof payload.sender_client_id === 'string' && payload.sender_client_id.trim();
  const msgType = typeof payload.msg_type === 'string' ? payload.msg_type.trim() : null;
  const hasEnvelope = typeof payload.envelope_b64 === 'string' && payload.envelope_b64.trim();
  const hasCiphertext = typeof payload.ciphertext_b64 === 'string' && payload.ciphertext_b64.trim();
  const requireEnvelope = msgType === 'application';
  const hasMessageBody = hasEnvelope && (!requireEnvelope || hasCiphertext);

  if (!hasSender || !hasMessageBody) {
    const details = {
      context,
      payload,
      originalKeys: input && typeof input === 'object' ? Object.keys(input) : [],
    };
    if (typeof console !== 'undefined' && typeof console.error === 'function') {
      try {
        console.error('[MLS] Aborting message POST: missing required MLS payload fields', details);
      } catch {}
    }
    const error = new Error('MLS message payload missing required fields');
    error.code = 'MLS_PAYLOAD_INVALID';
    error.context = context;
    error.payload = payload;
    throw error;
  }

  return {
    payload,
    body: JSON.stringify(payload),
  };
};

export default prepareMlsMessagePayload;
